import React, { Dispatch, FC, MutableRefObject, SetStateAction } from 'react';
import { Badge, Select } from '@crisellevaldez/speculo-ui';
import { CheckCircle, XCircle, Mail } from 'lucide-react';
import { HiSearch, HiGlobe } from 'react-icons/hi';
import { HiDevicePhoneMobile, HiComputerDesktop, HiDeviceTablet } from 'react-icons/hi2';
import { StatsModal } from '../StatsModal';
import { formatDate } from '../../../../utils/dateTime';
import { stripTrackingParams } from '../../../../utils/urlUtils';
import { getWebActivityEventTypeStyle } from '../../../../utils/badgeStyles';
import type { useOrgWebActivitiesGQL } from '../../../../hooks/graphql/dashboard/useOrgWebActivitiesGQL';

/* ─────────────────────────────────────────────────────────────────
 * Website Interactions drill-down — extracted from DashboardStats.
 * Shape is unchanged; the parent still owns filter state because the
 * same values are passed into `useDashboard()` to drive the GQL query
 * (changing filters here re-runs the parent's query upstream).
 * ───────────────────────────────────────────────────────────────── */

interface WebActivityItem {
  contactId?: string;
  isGuest?: boolean;
  guestInfo?: string;
  contactName?: string;
  deviceType?: string;
  pageUrl?: string;
  eventType?: string;
  eventCategory?: string;
  timestamp?: string;
  properties?: string;
  email?: string;
}

function tryParseGuestInfo(
  str: string | null | undefined,
): Record<string, string | null | undefined> | null {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function tryParseProps(str: string | null | undefined): Record<string, unknown> | null {
  if (!str) return null;
  if (typeof str === 'object') return str as Record<string, unknown>;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dateRange: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  timezone?: string;
  webActivitiesGQL: ReturnType<typeof useOrgWebActivitiesGQL>;
  webEventTypeFilter: string;
  setWebEventTypeFilter: Dispatch<SetStateAction<string>>;
  webDeviceFilter: string;
  setWebDeviceFilter: Dispatch<SetStateAction<string>>;
  webVisitorFilter: 'all' | 'identified' | 'guest';
  setWebVisitorFilter: Dispatch<SetStateAction<'all' | 'identified' | 'guest'>>;
  /** Parent owns the debounce so the same value reaches `useDashboard()`. */
  onSearch: (val: string) => void;
  webSearchTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  resetSearch: () => void;
  /** Opens the contact edit modal — parent owns that modal's state. */
  onOpenContact: (contactId: string) => void;
}

export const WebActivityStatsModal: FC<Props> = ({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  timezone,
  webActivitiesGQL,
  webEventTypeFilter,
  setWebEventTypeFilter,
  webDeviceFilter,
  setWebDeviceFilter,
  webVisitorFilter,
  setWebVisitorFilter,
  onSearch,
  webSearchTimerRef,
  resetSearch,
  onOpenContact,
}) => {
  if (!isOpen) return null;

  return (
    <StatsModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setWebEventTypeFilter('all');
        setWebDeviceFilter('all');
        setWebVisitorFilter('all');
        resetSearch();
        if (webSearchTimerRef.current) clearTimeout(webSearchTimerRef.current);
      }}
      title="Website Interactions"
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      onSearch={onSearch}
      searchPlaceholder="Search by name or page..."
      activeFilterCount={
        (webEventTypeFilter !== 'all' ? 1 : 0) +
        (webDeviceFilter !== 'all' ? 1 : 0) +
        (webVisitorFilter !== 'all' ? 1 : 0)
      }
      onResetFilters={() => {
        setWebEventTypeFilter('all');
        setWebDeviceFilter('all');
        setWebVisitorFilter('all');
        webActivitiesGQL.goToPage(0);
      }}
      currentPage={webActivitiesGQL.currentPage}
      pageSize={webActivitiesGQL.pageSize}
      onPageChange={webActivitiesGQL.goToPage}
      onNextPage={webActivitiesGQL.goToNextPage}
      onPrevPage={webActivitiesGQL.goToPrevPage}
      onPageSizeChange={webActivitiesGQL.changePageSize}
      totalCount={webActivitiesGQL.total}
      isLoading={webActivitiesGQL.isLoading}
      timezone={timezone}
      customFilters={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-[160px] shrink-0">
            <Select
              value={webEventTypeFilter}
              onChange={(v) => {
                setWebEventTypeFilter(Array.isArray(v) ? v[0] : v);
                webActivitiesGQL.goToPage(0);
              }}
              options={[
                { value: 'all', label: 'All Events' },
                { value: 'property_view', label: 'Property View' },
                { value: 'search', label: 'Search' },
                { value: 'lead_capture', label: 'Lead Capture' },
                { value: 'property_saved', label: 'Saved Property' },
              ]}
            />
          </div>
          <div className="w-[140px] shrink-0">
            <Select
              value={webDeviceFilter}
              onChange={(v) => {
                setWebDeviceFilter(Array.isArray(v) ? v[0] : v);
                webActivitiesGQL.goToPage(0);
              }}
              options={[
                { value: 'all', label: 'All Devices' },
                { value: 'desktop', label: 'Desktop' },
                { value: 'mobile', label: 'Mobile' },
                { value: 'tablet', label: 'Tablet' },
              ]}
            />
          </div>
          <div className="w-[160px] shrink-0">
            <Select
              value={webVisitorFilter}
              onChange={(v) => {
                const next = (Array.isArray(v) ? v[0] : v) as 'all' | 'identified' | 'guest';
                setWebVisitorFilter(next);
                webActivitiesGQL.goToPage(0);
              }}
              options={[
                { value: 'all', label: 'All Visitors' },
                { value: 'identified', label: 'Identified' },
                { value: 'guest', label: 'Guest' },
              ]}
            />
          </div>
        </div>
      }
      columns={[
        {
          key: 'loggedInStatus',
          header: 'Logged-In',
          width: '90px',
          isCentered: true,
          cell: (item: WebActivityItem) => {
            const isLoggedIn = !item.isGuest;
            const deviceIcons: Record<string, React.ReactNode> = {
              mobile: <HiDevicePhoneMobile className="h-4 w-4 text-primary" />,
              tablet: <HiDeviceTablet className="h-4 w-4 text-primary" />,
              desktop: <HiComputerDesktop className="h-4 w-4 text-primary" />,
            };
            const deviceIcon = deviceIcons[item.deviceType ?? ''] ?? (
              <HiGlobe className="h-4 w-4 text-primary" />
            );
            const deviceLabel = item.deviceType
              ? item.deviceType.charAt(0).toUpperCase() + item.deviceType.slice(1)
              : 'Unknown';
            return (
              <div className="flex justify-center items-center gap-2">
                {isLoggedIn ? (
                  <CheckCircle className="w-4 h-4 icon-green-check" aria-label="Logged In" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" aria-label="Guest" />
                )}
                <span title={deviceLabel} aria-label={deviceLabel} className="inline-flex">
                  {deviceIcon}
                </span>
              </div>
            );
          },
        },
        {
          key: 'contactName',
          header: 'Name',
          width: '220px',
          cell: (item: WebActivityItem) => {
            const guest = tryParseGuestInfo(item.guestInfo);
            const hasGuestData =
              item.isGuest && guest && (guest.firstName || guest.lastName || guest.email);
            const name =
              item.contactName ||
              (guest && `${guest.firstName || ''} ${guest.lastName || ''}`.trim()) ||
              '—';
            return (
              <div className="flex items-center gap-2">
                {item.isGuest && hasGuestData && (
                  <Badge className="badge-blue items-center gap-1 shrink-0">
                    <Mail className="w-3 h-3" />
                    from eAlert
                  </Badge>
                )}
                {item.contactId ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenContact(item.contactId as string);
                    }}
                    className="text-primaryfortext font-medium underline text-left truncate"
                  >
                    {name}
                  </button>
                ) : (
                  <span className="font-medium truncate">{name}</span>
                )}
              </div>
            );
          },
        },
        {
          key: 'guestEmail',
          header: 'Guest Email',
          width: '180px',
          cell: (item: WebActivityItem) => {
            const guest = tryParseGuestInfo(item.guestInfo);
            const email = guest?.email || item.email;
            return email ? (
              <span className="truncate">{email}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          },
        },
        {
          key: 'pageUrl',
          header: 'Page URL',
          width: '300px',
          cell: (item: WebActivityItem) => {
            const props = tryParseProps(item.properties);
            const cleanedUrl = stripTrackingParams(item.pageUrl || '');
            const isPropertyEvent = ['property_view', 'property_saved', 'lead_capture'].includes(
              item.eventType ?? '',
            );
            const isSearchEvent = item.eventType === 'search';
            if (isPropertyEvent && props?.address && props?.city) {
              const info = `${props.address}, ${props.city}${props.state ? `, ${props.state}` : ''}${props.zipCode ? ` ${props.zipCode}` : ''}`;
              return (
                <div className="max-w-[300px]">
                  <a
                    href={cleanedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline truncate block overflow-hidden text-ellipsis"
                    title={info}
                  >
                    {info}
                  </a>
                </div>
              );
            }
            if (isSearchEvent && props?.searchQuery) {
              return (
                <div className="max-w-[300px]">
                  <a
                    href={cleanedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline flex items-center gap-1.5 truncate overflow-hidden"
                    title={props.searchQuery as string}
                  >
                    <HiSearch className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{props.searchQuery as string}</span>
                  </a>
                </div>
              );
            }
            return (
              <div className="max-w-[300px]">
                <a
                  href={cleanedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline truncate block overflow-hidden text-ellipsis"
                  title={cleanedUrl}
                >
                  {cleanedUrl || '—'}
                </a>
              </div>
            );
          },
        },
        {
          key: 'eventType',
          header: 'Event Type',
          width: '220px',
          cell: (item: WebActivityItem) => {
            const eventCategory = item.eventCategory || '';
            let style;
            if (item.eventType === 'property_view') {
              style =
                item.pageUrl?.includes('valuation') || eventCategory === 'Valuation'
                  ? { background: 'web-evt-property-seller', text: '' }
                  : { background: 'web-evt-property-buyer', text: '' };
            } else {
              style = getWebActivityEventTypeStyle(item.eventType ?? '', eventCategory);
            }
            let label = item.eventType
              ? item.eventType
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c: string) => c.toUpperCase())
              : 'Unknown';
            if (item.eventType === 'property_view') {
              label =
                eventCategory === 'Valuation' || item.pageUrl?.includes('valuation')
                  ? 'Home Valuation - Seller'
                  : 'Property View - Buyer';
            }
            return <Badge className={`${style.background} ${style.text}`}>{label}</Badge>;
          },
        },
        {
          key: 'timestamp',
          header: 'Date/Time',
          width: '220px',
          cell: (item: WebActivityItem) =>
            item.timestamp ? (
              <span className="whitespace-nowrap">
                {formatDate(item.timestamp, timezone, 'MMM D, h:mm A z')}
              </span>
            ) : (
              '—'
            ),
        },
      ]}
      data={webActivitiesGQL.webActivities || []}
    />
  );
};

export default WebActivityStatsModal;
